import { Popover, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { fromDaysToBiggestAsPossible } from '@/utils/dateTime';

import { RetentionSelector } from './retentionSelector';

export const getHumanizeRetentionText = (retentionInDays: number) => {
  const formattedRetention = fromDaysToBiggestAsPossible(retentionInDays);
  const retentionText =
    formattedRetention.value === 1
      ? `a ${formattedRetention.unit.slice(0, -1)}`
      : `${formattedRetention.value} ${formattedRetention.unit}`;
  return retentionText;
};

export const RetentionPopover = ({
  retentionInDays,
  defaultRetentionInDays,
  minimumRetentionInDays = 1,
  maximumRetentionInDays,
  onUpdate,
  popoverTitle,
}: {
  retentionInDays?: number;
  defaultRetentionInDays?: number;
  minimumRetentionInDays?: number;
  maximumRetentionInDays?: number;
  onUpdate: (retentionInDays: number) => void;
  popoverTitle?: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const title =
    popoverTitle ||
    `Choose a retention time${maximumRetentionInDays ? ` (${minimumRetentionInDays} - ${maximumRetentionInDays} days)` : ''}`;

  const error = retentionInDays
    ? retentionInDays < minimumRetentionInDays
      ? `Retention time can't be shorter than ${getHumanizeRetentionText(minimumRetentionInDays)}`
      : maximumRetentionInDays && retentionInDays > maximumRetentionInDays
        ? `Retention time can't be more than ${getHumanizeRetentionText(maximumRetentionInDays)}`
        : ''
    : '';

  return (
    <>
      <Tooltip
        title={error}
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: 'var(--mui-palette-error-main)',
            },
          },
        }}
      >
        <ButtonWithIcon
          sx={{
            '&:hover': {
              borderColor: 'transparent',
            },
          }}
          variant={error ? 'outlined' : 'text'}
          ref={setAnchorEl}
          color={error ? 'error' : 'primary'}
          text={
            retentionInDays ? (
              getHumanizeRetentionText(retentionInDays)
            ) : (
              <Typography color='var(--mui-palette-text-disabled)'>
                Select
              </Typography>
            )
          }
          icon='material-symbols-arrow-drop-down-rounded'
          iconPosition='right'
          onClick={() => setPopoverOpen(true)}
          data-testid='create-backup-policy-retention-select'
          disableFocusRipple
        />
      </Tooltip>
      {popoverOpen && (
        <Popover
          disablePortal
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={() => {
            if (!retentionInDays) {
              onUpdate(defaultRetentionInDays || 0);
            }
            setPopoverOpen(false);
          }}
        >
          <Stack className='p-[24px]' gap='24px' width='300px'>
            <Typography variant='subtitle1'>{title}</Typography>
            <RetentionSelector
              minimumRetentionDays={minimumRetentionInDays}
              maximumRetentionDays={maximumRetentionInDays}
              retentionInDays={retentionInDays || defaultRetentionInDays}
              onChange={onUpdate}
            />
          </Stack>
        </Popover>
      )}
    </>
  );
};
